import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {motion} from 'framer-motion'
import {Button} from 'primereact/button'
import {useCallback, useEffect, useMemo} from 'react'
import {Responsive, WidthProvider} from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import barChartImage from '../../assets/images/barChartImage.png'
import datePickerImage from '../../assets/images/datePickerImage.png'
import notificationImage from '../../assets/images/notificationImage.png'
import pathwaysImage from '../../assets/images/patientByPathwaysImage.png'
import staticListImage from '../../assets/images/patientByStaticListImage.png'
import pieChartImage from '../../assets/images/pieChartImage.png'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import useWindowSize from '../../utils/useWindowSize'
import DatePicker from '../../views/DatePicker'
import {useAuth} from '../authentication/AuthProvider'
import {getUserId} from '../authentication/AuthUtils'
import BarChart from '../chart/BarChart'
import PieCustomChart from '../chart/PieCustomChart'
import PageContentWrapper from '../layout/PageContentWrapper'
import {severityNotification} from '../notification/NotificationConstant'
import Notifications from '../notification/Notifications'
import {showNotification} from '../notification/NotificationService'
import PatientByPathwayTable from '../pathway/PatientByPathwayTable'
import {getCustomPatientList} from '../patient/PatientEndPoint'
import {useRoleContext} from '../role/RoleProvider'
import PatientByStaticList from '../static-list/PatientByStaticList'
import {
    getAdvancedFilter,
    getShouldRememberAdvancedFilter,
    tableTypes,
} from '../styled-component/StyledTableUtils'
import {createOrUpdateWidget} from './DnDEnpoint'
import './DnDStyle.css'

const ResponsiveReactGridLayout = WidthProvider(Responsive)
const fontSizeResponsive = 'md:text-md lg:text-md text-xs'

const DnDLayout = ({cols = {lg: 12, md: 12, sm: 8, xs: 8, xxs: 4}}) => {
    useSignals()
    //#region Context
    const {user} = useAuth()
    const {userRole, widget, timezoneId} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {patientManagementPermissions, isQualifiedForTakingCareOfPatients} =
        useRoleContext()
    const queryClient = useQueryClient()
    //#endregion

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    const mapKeyToComponent = key => {
        switch (key) {
            case 'pie-chart':
                return <PieCustomChart />
            case 'bar-chart':
                return <BarChart />
            case 'notification':
                return <Notifications />
            case 'patient-by-pathway':
                return <PatientByPathwayTable />
            case 'patient-by-static-list':
                return <PatientByStaticList />
            case 'date-picker':
                return <DatePicker />
            default:
                break
        }
    }
    const layoutList = useMemo(() => {
        if (!!widget && typeof widget === 'string') {
            const layouts = JSON.parse(widget)
            if (
                !!layouts &&
                typeof layouts === 'object' &&
                Object.keys(layouts)?.length > 0
            ) {
                for (const layout in layouts) {
                    if (!!layout && layouts[layout]?.length > 0) {
                        for (let i = 0; i < layouts[layout].length; i++) {
                            layouts[layout][i]['content'] = mapKeyToComponent(
                                layouts[layout][i].key,
                            )
                        }
                    }
                }

                return layouts
            } else {
                return {
                    lg: [],
                    md: [],
                    sm: [],
                    xs: [],
                    xxs: [],
                }
            }
        } else {
            return {
                lg: [],
                md: [],
                sm: [],
                xs: [],
                xxs: [],
            }
        }
    }, [widget])

    const widgetList = useMemo(() => {
        return [
            {
                w: 4,
                h: 9,
                minH: 9,
                minW: 4,
                key: 'pie-chart',
                label: 'Pie Chart',
                icon: pieChartImage,
            },
            {
                w: 4,
                h: 8,
                minW: 4,
                minH: 8,
                label: 'Bar Chart',
                key: 'bar-chart',
                icon: barChartImage,
            },
            {
                w: 4,
                h: 9,
                minW: 4,
                minH: 9,
                maxH: 10,
                label: 'Notification',
                key: 'notification',
                icon: notificationImage,
            },
            {
                w: 4,
                h: 8,
                minW: 4,
                minH: 8,
                label: 'Patient By Pathway',
                key: 'patient-by-pathway',
                icon: pathwaysImage,
            },
            {
                w: 4,
                h: 8,
                minW: 4,
                minH: 8,
                label: 'Patient By Static List',
                key: 'patient-by-static-list',
                icon: staticListImage,
            },
            {
                w: 4,
                h: 2,
                minW: 4,
                minH: 2,
                maxH: 2,
                label: 'Calendar',
                key: 'date-picker',
                icon: datePickerImage,
            },
        ]
    }, [])

    const size = useWindowSize()

    const currentBreakpoint = useMemo(() => {
        if (size >= 1200) {
            return 'lg'
        } else if (size >= 996) {
            return 'md'
        } else if (size >= 768) {
            return 'sm'
        } else if (size >= 480) {
            return 'xs'
        } else {
            return 'xxs'
        }
    }, [size])

    const state = useSignal({
        currentBreakpoint: currentBreakpoint,
        compactType: 'vertical',
        mounted: true,
        layouts: {
            ...layoutList,
        },
    })
    const isLockDrag = useSignal(true)
    const itemDraggingOutside = useSignal(null)

    const handleOnClickItem = useCallback(
        (key, data) => {
            if (key === 'remove') {
                state.value = {
                    ...state.value,
                    layouts: {
                        ...state.value.layouts,
                        [state.value.currentBreakpoint]: state.value.layouts[
                            state.value.currentBreakpoint
                        ].filter(({key}) => key !== data.key),
                    },
                }
            }
        },
        [state],
    )

    const generateDOM = useCallback(() => {
        return (
            state.value?.layouts &&
            state.value?.layouts[state.value.currentBreakpoint] &&
            state.value?.layouts[state.value.currentBreakpoint]?.length > 0 &&
            state.value?.layouts[state.value.currentBreakpoint].map((l, i) => {
                return (
                    <div
                        key={l.i}
                        className={`${
                            !isLockDrag.value ? '' : 'select-none'
                        } z-5`}>
                        {!isLockDrag.value && (
                            <div
                                className={`p-inputgroup flex justify-content-end w-full absolute m-0 p-2`}
                                style={{
                                    zIndex: '5',
                                }}>
                                <Button
                                    icon="pi pi-trash"
                                    className="border-circle p-button-danger "
                                    onClick={() =>
                                        handleOnClickItem('remove', l)
                                    }
                                    onTouchEnd={() =>
                                        handleOnClickItem('remove', l)
                                    }
                                />
                            </div>
                        )}
                        <div className="h-full w-full relative">
                            {!!l?.content
                                ? l?.content
                                : mapKeyToComponent(l?.key)}
                            {!isLockDrag && (
                                <div className="h-full w-full absolute top-0 surface-500 opacity-10"></div>
                            )}
                        </div>
                    </div>
                )
            })
        )
    }, [handleOnClickItem, isLockDrag, state])

    const onBreakpointChange = breakpoint => {
        state.value = {
            ...state.value,
            currentBreakpoint: breakpoint,
        }
    }

    const onLayoutChange = (layout, layoutItem, event) => {
        let tempList = [...state.value?.layouts[state.value?.currentBreakpoint]]

        // Map "key" from old layout list
        for (let i = 0; i < layout.length; i++) {
            layout[i]['key'] = tempList[i].key
            layout[i]['content'] = mapKeyToComponent(tempList[i].key)
            layout[i]['static'] = tempList[i].static
        }

        // Sort layouts and set "i"
        let sortedList = layout.sort((a, b) => a.y - b.y)

        // sortedList.forEach((element, idx) => (element['i'] = idx.toString()))

        state.value = {
            ...state.value,
            layouts: {
                lg: [...sortedList],
                md: [...sortedList],
                sm: [...sortedList],
                xs: [...sortedList],
                xxs: [...sortedList],
            },
        }
    }

    const onResize = layouts => {
        let tempList = [...state.value?.layouts[state.value?.currentBreakpoint]]
        // Map "key" from old layout list
        for (let i = 0; i < layouts.length; i++) {
            layouts[i]['key'] = tempList[i].key
            layouts[i]['content'] = mapKeyToComponent(tempList[i].key)
            layouts[i]['static'] = tempList[i].static
        }

        // Sort layouts and set "i"
        let sortedList = layouts.sort((a, b) => a.y - b.y)

        // sortedList.forEach((element, idx) => (element['i'] = idx.toString()))

        state.value = {
            ...state.value,
            layouts: {
                lg: [...sortedList],
                md: [...sortedList],
                sm: [...sortedList],
                xs: [...sortedList],
                xxs: [...sortedList],
            },
        }
    }

    const onDrop = (layout, layoutItem, event) => {
        if (!isLockDrag.value) {
            if (
                !!state.value?.layouts &&
                !!state.value.layouts[state.value?.currentBreakpoint]
            ) {
                // Remove last item (new item drop)
                layout.pop()
                let tempList = [
                    ...state.value?.layouts[state.value?.currentBreakpoint],
                ]

                // Map "key" from old layout list
                for (let i = 0; i < layout.length; i++) {
                    layout[i]['key'] = tempList[i]?.key
                    layout[i]['content'] = mapKeyToComponent(tempList[i]?.key)
                    layout[i]['static'] = tempList[i]?.static
                }

                // Get data from new drag item and set ...data to layoutItem
                let dragData = JSON.parse(
                    event.dataTransfer.getData('drag-item'),
                )
                if (
                    state.value?.layouts &&
                    state.value?.layouts[state.value?.currentBreakpoint] &&
                    !state.value?.layouts[state.value?.currentBreakpoint]?.some(
                        layoutItem => layoutItem?.key === dragData?.key,
                    )
                ) {
                    if (!!dragData) {
                        layoutItem['key'] = dragData?.key
                        layoutItem['content'] = mapKeyToComponent(dragData?.key)
                        layoutItem['h'] = dragData?.h
                        layoutItem['w'] = dragData?.w
                        layoutItem['minW'] = dragData?.minW
                        layoutItem['minH'] = dragData?.minH
                        layoutItem['maxW'] = dragData?.maxW
                        layoutItem['maxH'] = dragData?.maxH
                        layoutItem['isDraggable'] = null
                        layoutItem['static'] = false
                    }

                    // Push layout item to layout
                    layout.push(layoutItem)

                    // Sort layouts and set "i"
                    let sortedList = layout?.sort((a, b) => a.y - b.y)

                    sortedList.forEach(
                        (element, idx) => (element['i'] = idx.toString()),
                    )

                    state.value = {
                        ...state.value,
                        layouts: {
                            lg: [...sortedList],

                            md: [...sortedList],
                            sm: [...sortedList],
                            xs: [...sortedList],
                            xxs: [...sortedList],
                            // [state?.currentBreakpoint]: [...sortedList],
                        },
                    }
                }

                itemDraggingOutside.value = null
            }
        }
    }

    const onDragStart = (element, item) => {
        let data = {
            key: item?.key,
            h: item?.h,
            w: item?.w,
            minW: item?.minW,
            minH: item?.minH,
            maxW: item?.maxW,
            maxH: item?.maxH,
        }

        element.dataTransfer.setData('drag-item', JSON.stringify(data))

        itemDraggingOutside.value = {
            ...data,
            i: 'drag-item',
        }
    }

    const handleOnSaveLayouts = useCallback(async () => {
        isLockDrag.value = !isLockDrag.value

        if (!isLockDrag.value) {
            return
        }

        if (!!state.value) {
            let layoutsSave = {
                lg: [],
                md: [],
                sm: [],
                xs: [],
                xxs: [],
            }

            if (
                state.value?.layouts &&
                Object.keys(state.value.layouts).length > 0
            ) {
                layoutsSave = {...state.value.layouts}
                let isLayoutEmpty = false
                for (const layout in layoutsSave) {
                    if (layoutsSave[layout]?.length > 0) {
                        for (let i = 0; i < layoutsSave[layout].length; i++) {
                            delete layoutsSave[layout][i]?.content
                        }
                    } else {
                        isLayoutEmpty = true
                        break
                    }
                }

                if (isLayoutEmpty) {
                    layoutsSave = {
                        lg: [],
                        md: [],
                        sm: [],
                        xs: [],
                        xxs: [],
                    }
                }
            }

            const layoutsString = JSON.stringify(layoutsSave)

            let toPostData = {
                widget: layoutsString,
            }

            const response = await createOrUpdateWidget(getUserId(), toPostData)
            if (response?.id) {
                showNotification(
                    severityNotification.success,
                    'Update Dashboard successful',
                )

                const {appConfigKeys} = QueryKeyManager(
                    QueryKeyTypes.appConfiguration,
                    user?.id,
                )

                queryClient.invalidateQueries(appConfigKeys.pattern())
            }
        }
    }, [isLockDrag, state.value])

    const isDraggable = key => {
        return (
            !isLockDrag.value &&
            state.value.layouts &&
            state.value.layouts[state.value.currentBreakpoint] &&
            !state.value.layouts[state.value.currentBreakpoint]?.some(
                layoutItem => layoutItem?.key === key,
            )
        )
    }

    //#region Prefetch User Table data

    //#region Load Data from Local Storage
    const patientInitialFilterRequest = useMemo(() => {
        const didRememberSettings = getShouldRememberAdvancedFilter(
            tableTypes.patient,
        )

        let parsedStoredFilterRequest = {}

        if (didRememberSettings) {
            const storedFilterRequest = getAdvancedFilter(tableTypes.patient)
            if (
                !!storedFilterRequest &&
                Object.keys(storedFilterRequest).length > 0
            ) {
                parsedStoredFilterRequest = {...storedFilterRequest}
            }
        }

        return {
            globalSearchValue: null,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            systemRole: SystemRole.USER,
            hcpIds: [],
            sicknessLevels: [],
            pathwayIds: [],
            staticListIds: [],
            ...parsedStoredFilterRequest,
            tableViewType: isQualifiedForTakingCareOfPatients
                ? 'all'
                : 'only_assigned',
        }
    }, [])
    //#endregion

    useEffect(() => {
        const {patientKeys} = QueryKeyManager(
            QueryKeyTypes.patient,
            getUserId(),
        )

        queryClient.prefetchQuery({
            queryKey: patientKeys.customList({
                hospitalId:
                    userRole === SystemRole.USER_WITH_ROLE
                        ? currentHospital?.id
                        : null,
                timezoneId: timezoneId,
                pageNumber: 0,
                pageRows: 10,
                sortField: 'sicknessLevel',
                sortOrder: -1,
                filterRequest: patientInitialFilterRequest,
            }),
            queryFn: () =>
                getCustomPatientList(
                    getUserId(),
                    0,
                    10,
                    'sicknessLevel',
                    -1,
                    userRole === SystemRole.USER_WITH_ROLE &&
                        !!currentHospital?.id
                        ? {
                              ...patientInitialFilterRequest,
                              hospitalId: currentHospital?.id,
                          }
                        : {...patientInitialFilterRequest},
                ),
        })
    }, [])

    return (
        <PageContentWrapper>
            <div id="dndLayoutWrapper" className="h-full">
                {/* List Widget */}
                {!isLockDrag.value && (
                    <>
                        <div className="pt-2 px-3">
                            <h5 className="font-bold m-0">Widgets</h5>
                            <span>
                                Drag and drop the widgets you need into the main
                                page.
                            </span>
                        </div>
                        <div className="flex flex-wrap justify-content-between align-items-center border-300 border-bottom-2 mb-2">
                            <motion.div
                                layout
                                className="flex align-items-center gap-2 overflow-y-auto w-max">
                                {widgetList &&
                                    widgetList?.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className={`droppable-element border-round-lg flex flex-column align-items-center justify-content-around surface-100 ${
                                                isDraggable(item.key)
                                                    ? ''
                                                    : 'select-none opacity-50'
                                            }`}
                                            style={{
                                                height: '10rem',
                                                width: '10rem',
                                            }}
                                            draggable={isDraggable(item.key)}
                                            unselectable="on"
                                            onDragStart={e =>
                                                onDragStart(e, item)
                                            }>
                                            <div className="w-8rem">
                                                <img
                                                    className="unselectable select-none w-full border-round"
                                                    src={item?.icon}
                                                    alt={item?.key}
                                                    height={80}
                                                    style={{
                                                        objectFit: 'contain',
                                                    }}
                                                />
                                            </div>

                                            {/* TODO: translate */}
                                            <span
                                                className={`font-bold ${fontSizeResponsive}`}>
                                                {item.label}
                                            </span>
                                        </div>
                                    ))}
                            </motion.div>
                        </div>
                    </>
                )}
                <div className="col-12 flex justify-content-end">
                    <Button
                        className={`${
                            isLockDrag.value
                                ? 'p-button-warning'
                                : 'p-button-success'
                        } ${fontSizeResponsive}`}
                        icon={`${
                            isLockDrag.value ? 'pi pi-lock' : 'pi pi-lock-open'
                        }`}
                        label={`${
                            isLockDrag.value
                                ? translateCommons('Unlock to modify widgets')
                                : translateCommons('Lock to save modified')
                        }`}
                        onClick={handleOnSaveLayouts}
                    />
                </div>
                <ResponsiveReactGridLayout
                    style={{minHeight: '40rem'}}
                    layouts={state.value?.layouts || []}
                    onBreakpointChange={onBreakpointChange}
                    // onLayoutChange={onLayoutChange}
                    onDragStop={onLayoutChange}
                    onDrop={onDrop}
                    onResizeStop={onResize}
                    measureBeforeMount={false}
                    useCSSTransforms={state.value?.mounted}
                    compactType={state.value?.compactType}
                    droppingItem={itemDraggingOutside.value || null}
                    items={40}
                    rowHeight={50}
                    cols={cols}
                    breakpoints={{lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0}}
                    isDroppable={!isLockDrag.value}
                    isDraggable={!isLockDrag.value}
                    isResizable={!isLockDrag.value}
                    // preventCollision={true}
                >
                    {generateDOM()}
                </ResponsiveReactGridLayout>
            </div>
        </PageContentWrapper>
    )
}

export default DnDLayout
